import { DotmSquare11 } from '~/components/ui/dotm-square-11';
import { cn } from '~/lib/utils';
import themes from '~/design-system/components/v1_5/Header/HeaderTheme.module.css';
import styles from './GeneratingPill.module.css';
import type { HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface GeneratingPillProps {
  /** Verbatim: `Thinking` before there is anything to write, `Writing the artifact` after. */
  label: string;
  /**
   * 0–100. Omitted before writing starts, on purpose: a percentage with no real denominator is a
   * guess, and the moment it appears the user starts timing it.
   */
  percent?: number;
  theme?: HeaderTheme;
  className?: string;
}

/**
 * The pill that says the artifact is being made, pinned to the artifact's own top-right corner.
 *
 * Anchored to the artifact rather than to the pane — right edges flush, floating 5px above the top
 * edge — because it is a fact about the artifact and not about the pane. That is also why it is the
 * one floating thing here that is NOT positioned against the stage: it moves when the artifact does.
 *
 * `role="status"` because this is the only place the pane says anything about progress, and a label
 * that only exists visually leaves a screen reader on a stage that never changes. It sits on the
 * LABEL rather than on the pill: `role="status"` implies `aria-atomic`, so with the counter inside
 * the region a host stepping `percent` every 300ms queues a hundred-odd announcements of
 * "Writing the artifact 62%", each identical to the last but for two digits. Around the label alone
 * it speaks twice in the life of an artifact — once at `Thinking`, once at `Writing the artifact`.
 */
export function GeneratingPill({ label, percent, theme: themeProp, className }: GeneratingPillProps) {
  const theme = useV15Theme(themeProp);
  return (
    <div className={cn(themes[theme], styles.pill, className)}>
      {/* THE LIVE MATRIX, not a sparkle. Guri, 2026-09-04: "replace that star icon with dot matrix we
          use for everywhere in generation". This is the same `DotmSquare11` every other in-flight state
          in the product shows — it ripples its dots outward by Manhattan distance and has its own
          reduced-motion path — and it settles a rule already written down once in `ThinkingIndicator`:
          the agent working is the matrix, never the sparkle, and there is exactly one matrix. A static
          glyph here was the last place the two vocabularies disagreed. */}
      <DotmSquare11 className={styles.mark} size={14} dotSize={2} aria-hidden />
      <span className={styles.label} role="status">
        {label}
      </span>
      {/* Hidden from the accessible name as well as from the live region: the 7px between the two
          spans is flex `gap`, which contributes no whitespace, so an exposed counter would read out
          as `Writing the artifact62%`. It is a number that counts up while you watch it — there is
          nothing in it for a screen reader that the label has not already said. */}
      {percent === undefined ? null : (
        <span className={styles.percent} aria-hidden>
          {Math.round(percent)}%
        </span>
      )}
    </div>
  );
}
